import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Label,
  TextInput,
  Button,
  Progress,
  useThemeMode
} from "flowbite-react";
import { HiSearch } from "react-icons/hi";
import { useLazyGetChannelDistributionReportQuery } from "../../api/reportsApi/reports";
import { useReportCompanyParam } from "../../hooks/useReportCompanyParam";
import DateRangePresets from "../../components/reports/DateRangePresets";
import { getErrorMessage } from "../../utils/requestUtils";
import { toast } from "react-toastify";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

const ChannelDistributionReport: FC = () => {
  const { t } = useTranslation(["reports"]);
  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  const today = new Date().toISOString().split("T")[0];

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  
  const [triggerReport, { data: reportData, isLoading }] = useLazyGetChannelDistributionReportQuery();
  const companyParam = useReportCompanyParam();

  const handleSearch = async () => {
    if (!startDate || !endDate) {
      toast.warn(t("selectClientDateWarning"));
      return;
    }

    if (startDate > endDate) {
      toast.error(t("startDateHigherThanEndDate"));
      return;
    }

    try {
      await triggerReport({
        start_date: startDate,
        end_date: endDate,
        ...companyParam
      }).unwrap();
    } catch (error: any) {
       toast.error(getErrorMessage(error));
    }
  };

  const summaryCards = [
    { label: t("summary.totalSessions"), value: reportData?.summary.total_widget_sessions ?? 0, color: "text-blue-600" },
    { label: t("summary.resolvedByAi"), value: reportData?.summary.resolved_by_ai_agent ?? 0, color: "text-green-600" },
    { label: t("summary.ticketsCreated"), value: reportData?.summary.resolved_by_ticket ?? 0, color: "text-purple-600" },
    { label: t("summary.transferredToLive"), value: reportData?.summary.transferred_to_live_agent ?? 0, color: "text-orange-600" },
    { label: t("summary.ticketsFromMail"), value: reportData?.summary.tickets_from_mail ?? 0, color: "text-indigo-600" },
  ];

  const pieChartOptions: ApexOptions = { 
    chart: {
      type: "donut",
       background: "transparent",
    },
    labels: [t("summary.resolvedByAi"), t("summary.ticketsCreated"), t("summary.transferredToLive")],
    colors: ["#16bdca", "#7e3af2", "#fdba8c"], 
    legend: {
      position: "bottom",
      labels: {
          colors: isDark ? "#fff" : "#000",
      }
    },
    dataLabels: {
      enabled: true,
    },
     theme: {
        mode: isDark ? 'dark' : 'light', 
    }
  };

  const pieChartSeries = [
    reportData?.summary.resolved_by_ai_agent ?? 0,
    reportData?.summary.resolved_by_ticket ?? 0,
    reportData?.summary.transferred_to_live_agent ?? 0,
  ];

  const barChartOptions: ApexOptions = { 
    chart: {
      type: "bar",
      background: "transparent",
      toolbar: {
        show: false
      }
    },
    xaxis: {
      categories: [
          t("summary.totalSessions"), 
          t("summary.resolvedByAi"), 
          t("summary.ticketsCreated"), 
          t("summary.transferredToLive"), 
          t("summary.ticketsFromMail")
      ],
      labels: {
        style: {
          colors: isDark ? "#fff" : "#000",
        }
      }
    },
     yaxis: {
      labels: {
        style: {
          colors: isDark ? "#fff" : "#000",
        }
      }
    },
    colors: ["#1a56db", "#16bdca", "#7e3af2", "#fdba8c", "#6366f1"],
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '50%',
        distributed: true,
      }
    },
    dataLabels: {
      enabled: false
    },
     grid: {
        borderColor: isDark ? '#374151' : '#e5e7eb',
     },
     theme: {
        mode: isDark ? 'dark' : 'light',
    }
  };

  const barChartSeries = [{
    name: "Sessions",
    data: [
      reportData?.summary.total_widget_sessions ?? 0,
      reportData?.summary.resolved_by_ai_agent ?? 0,
      reportData?.summary.resolved_by_ticket ?? 0,
      reportData?.summary.transferred_to_live_agent ?? 0,
      reportData?.summary.tickets_from_mail ?? 0,
    ]
  }];

  return (
    <div className="w-full">
      <div className="px-4 pt-6">
        <Card className="mb-6">
            <DateRangePresets
              className="mb-4"
              startDate={startDate}
              endDate={endDate}
              onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                 <div>
                    <div className="mb-2 block">
                        <Label htmlFor="startDate" value={t("startDate")} />
                    </div>
                    <TextInput
                        id="startDate"
                        type="date"
                        value={startDate}
                        max={today}
                        onChange={(e) => setStartDate(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key !== "Tab") e.preventDefault();
                        }}
                        required
                    />
                </div>
                 <div>
                    <div className="mb-2 block">
                        <Label htmlFor="endDate" value={t("endDate")} />
                    </div>
                    <TextInput
                        id="endDate"
                        type="date"
                        value={endDate}
                        max={today}
                        onChange={(e) => setEndDate(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key !== "Tab") e.preventDefault();
                        }}
                        required
                    />
                </div>
                <Button color="primary" onClick={handleSearch} disabled={isLoading} isProcessing={isLoading}>
                    <HiSearch className="mr-2 h-5 w-5" />
                    {t("generateReport")}
                </Button>
            </div>
        </Card>

        {reportData && (
            <div className="grid gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {summaryCards.map((card, index) => (
                        <Card key={index}>
                            <h5 className="text-sm tracking-tight text-gray-500 dark:text-gray-400">
                                {card.label}
                            </h5>
                            <p className={`text-2xl font-bold ${card.color} dark:text-white`}>
                                {card.value}
                            </p>
                        </Card>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                     <Card>
                        <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                            {t("charts.sessionDistribution")}
                        </h5>
                        <div className="h-80">
                            <ReactApexChart 
                                options={pieChartOptions} 
                                series={pieChartSeries} 
                                type="donut" 
                                height="100%" 
                            />
                        </div>
                    </Card>
                    <Card>
                        <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                            {t("charts.metricsOverview")}
                        </h5>
                         <div className="h-80">
                            <ReactApexChart 
                                options={barChartOptions} 
                                series={barChartSeries} 
                                type="bar" 
                                height="100%" 
                            />
                        </div>
                    </Card>
                </div>

                 <Card className="w-[600px] mx-auto">
                    <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                        {t("charts.performanceRates")}
                    </h5>
                    <div className="space-y-6">
                        <div>
                            <div className="mb-1 flex justify-between font-medium dark:text-white">
                                <span>{t("charts.aiResolutionRate")}</span>
                                <span>{reportData.percentages.ai_agent_rate}%</span>
                            </div>
                            <Progress progress={reportData.percentages.ai_agent_rate} color="teal" size="lg" />
                        </div>
                        <div>
                            <div className="mb-1 flex justify-between font-medium dark:text-white">
                                <span>{t("charts.ticketCreationRate")}</span>
                                <span>{reportData.percentages.ticket_rate}%</span>
                            </div>
                            <Progress progress={reportData.percentages.ticket_rate} color="purple" size="lg" />
                        </div>
                         <div>
                            <div className="mb-1 flex justify-between font-medium dark:text-white">
                                <span>{t("charts.liveTransferRate")}</span>
                                <span>{reportData.percentages.live_agent_rate}%</span>
                            </div>
                            <Progress progress={reportData.percentages.live_agent_rate} color="blue" size="lg" />
                        </div>
                    </div>
                 </Card>
            </div>
        )}
      </div>
    </div>
  );
};

export default ChannelDistributionReport;
